import axios from "axios";
import { SERVER_URL } from "Constants";
import useAsync from "Hooks/useAsync"
import alertToast from "Util/ToastifyUtils";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { GenerateCreditReportRequestSummary, GenerateCreditReportStage, SearchEntityDetail } from "../../GenerateCreditReport";

type StripePaymentResponse = {
	paymentIntentId: string;
	status: string;
	paymentMethodId: string;
	declineCode: string;
	reportRequestIds: string[];
}

async function initiateStripePayment(summary: GenerateCreditReportRequestSummary, paymentMethodId?: string, customerId?: string): Promise<StripePaymentResponse> {
	function getLookupName(entityDetail?: SearchEntityDetail): string {
		if (!entityDetail) return "";
		if ("entityName" in entityDetail) return entityDetail.entityName;
		if ("name" in entityDetail) return entityDetail.name;
		return "";
	}

	try {
		const response = await axios.post<StripePaymentResponse>(`${SERVER_URL}/api/stripe/credit-report-payment`, {
			paymentMethodId: paymentMethodId,
			creditReportSubject: {
				lookupValue: summary.requestEntity.lookupDetail?.lookupValue ?? "",
				lookupMethod: summary.requestEntity.lookupDetail?.lookupMethod ?? "",
				countryCode: summary.countryCode.countryCode,
				entityName: getLookupName(summary.requestEntity.entityDetail),
			},
			creditReportBillingEntity: {
				organisationId: summary.billingEntity.organisationId,
				businessEntityId: summary.billingEntity.billingEntityId,
				businessEntityName: summary.billingEntity.billingEntityName,
				accountType: summary.billingEntity.billingType,
			},
			creditReportProducts: summary.selectedCreditReports.map(report => ({
				businessEntityCreditReportSettingsEntityId: report.id,
				name: report.name,
				bureau: report.bureau,
				type: report.type,
				previewLink: report.previewLink,
				originalPrice: report.originalPrice,
				price: report.price,
				discount: report.discount,
				recommended: report.recommended,
				available: report.available,
			})),
			customerId: customerId,
			monitorPaygPriceId: summary.monitorCustomer?.id ? summary.monitorCustomer.id || undefined : undefined,
			addNewCustomer: summary.monitorCustomer?.addCustomer ?? false,
		});
		const data = response.data;
		return data;
	} catch (error) {
		console.error(error);
		const response: StripePaymentResponse = {
			paymentIntentId: "",
			status: "failed",
			paymentMethodId: "",
			declineCode: "",
			reportRequestIds: [],
		}
		return response;
	}
}

async function handlePaymentFlow(summary: GenerateCreditReportRequestSummary, customerId?: string): Promise<StripePaymentResponse | null> {
	try {
		return await initiateStripePayment(summary, summary.billingEntity.paymentMethodId, customerId);
	} catch (error) {
		console.error(error);
		return null;
	}
}

type Props = {
	updateDetail: (status: "pending" | "success" | "failed", reportRequestIds: string[]) => void;
	generateCreditReportSummary: GenerateCreditReportRequestSummary;
	changeStage: (newStage: GenerateCreditReportStage) => void;
	customerId?: string;
}

export const MakePaymentForReport: React.FunctionComponent<Props> = ({
	updateDetail,
	generateCreditReportSummary,
	changeStage,
	customerId
}) => {
	const response = useAsync(
		async () => {
			if (
				generateCreditReportSummary.paymentStatus === 'success' &&
				generateCreditReportSummary.reportRequestIds.length === generateCreditReportSummary.selectedCreditReports.length
			){
				changeStage('generate');
				return null;
			}
			const result = await handlePaymentFlow(generateCreditReportSummary, customerId);

			if (result && result.status === 'succeeded'){
				updateDetail('success', result.reportRequestIds);
				changeStage('generate');
			} else {
				alertToast('Failed to process the payment. Please try again later.', 'error');
				updateDetail('failed', []);
				changeStage('review');
			}
			return result;
		},
		[]
	);

	return (
		<>
			{response.type === 'loading' && (
				<>
					<InlineSpinner />
					<p style={{display: "flex", justifyContent: "center"}}>Verifying payment...</p>
				</>
			)}
		</>
	)
}
